/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import EditApplicationModal from '../user/EditApplicationModal';
import { getUserDisplayName, getUserDisplayEmail, getUserStatusDisplay, isUserDeleted } from '../../utils/userUtils';
import { displayAccountNumber, formatDateSafe } from '../../utils/formatUtils';
import '../../styles/SavedApplications.css';
import { formatClock } from '../../utils/timeFormat';

const SavedApplications = ({ user }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewingApplication, setViewingApplication] = useState(null);
  const [editingApplication, setEditingApplication] = useState(null);
  const navigate = useNavigate();

  const fetchApplications = useCallback(async () => {
    try {
      const response = await axios.get('/api/applications/saved');
      setApplications(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('Failed to fetch applications.');
      setLoading(false);
    }
  }, []);

  const handleView = (application) => {
    setViewingApplication(application);
  };

  const handleEdit = (application) => {
    setEditingApplication(application);
  };

  const handleEditSave = (updatedApplication) => {
    // Update the application in the list
    setApplications(prev => 
      prev.map(app => 
        app._id === updatedApplication._id ? updatedApplication : app
      )
    );
  };

  const handleEditClose = () => {
    setEditingApplication(null);
  };

  const handleDelete = async (applicationId) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await axios.delete(`/api/applications/${applicationId}`);
        fetchApplications();
      } catch (error) {
        console.error('Error deleting application:', error);
        setError('Failed to delete application');
      }
    }
  };

  const handleWithdraw = async (applicationId) => {
    if (window.confirm('Are you sure you want to withdraw this application?')) {
      try {
        await axios.put(`/api/applications/${applicationId}/withdraw`);
        fetchApplications();
      } catch (error) {
        console.error('Error withdrawing application:', error);
        setError('Failed to withdraw application');
      }
    }
  };

  // Refetch when the signed-in user changes; the endpoint is session-scoped.
  useEffect(() => {
    fetchApplications();
  }, [fetchApplications, user?.email]);

  const ApplicationView = ({ application, onClose }) => {
    if (!application) return null;

    const handleOverlayClick = (e) => {
      if (e.target.className === 'modal-overlay') {
        onClose();
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'Not specified';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const getStatusBadgeClass = (status) => {
      const statusClasses = {
        'Saved': 'status-saved',
        'Submitted': 'status-submitted',
        'Approved': 'status-approved',
        'Rejected': 'status-rejected',
        'In Progress': 'status-progress',
        'Completed': 'status-completed',
        'Expired': 'status-expired',
        'Terminated': 'status-terminated',
        'Revision Required': 'status-revision',
        'Revised': 'status-revised'
      };
      return statusClasses[status] || 'status-default';
    };

    return (
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className="modal-content">
          <div className="modal-header">
            <div className="header-content">
              <h2>Project Application Details</h2>
              <span className={`status-badge ${getStatusBadgeClass(application.project_status)}`}>
                {application.project_status}
              </span>
            </div>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          
          <div className="project-info">
            {/* Basic Information Section */}
            <div className="info-section">
              <div className="section-header">
                <h3><i className="icon-info"></i>Basic Information</h3>
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <label>Project Title</label>
                  <p>{application.project_title || 'Not specified'}</p>
                </div>
                <div className="info-item">
                  <label>Proposal ID</label>
                  <p>{application.project_id || 'Not assigned'}</p>
                </div>
                <div className="info-item">
                  <label>Start Date</label>
                  <p>{formatDate(application.project_start_date)}</p>
                </div>
                <div className="info-item">
                  <label>End Date</label>
                  <p>{formatDate(application.project_end_date)}</p>
                </div>
              </div>
            </div>

            {/* Facility Information */}
            <div className="info-section">
              <div className="section-header">
                <h3><i className="icon-facility"></i>Facility Information</h3>
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <label>Facility Name</label>
                  <p>{application.facility?.facility_name || 'Not specified'}</p>
                </div>
                <div className="info-item">
                  <label>Preferred Space</label>
                  <p>{application.preferred_space || 'Not specified'}</p>
                </div>
                <div className="info-item">
                  <label>Pot Requirements</label>
                  <div className="pot-requirements">
                    {application.pots && application.pots.length > 0 ? (
                      application.pots.map((pot, index) => (
                        <div key={index} className="pot-item">
                          <span className="pot-size">{pot.pot_size?.pot || 'Unknown Size'}</span>
                          <span className="pot-quantity">× {pot.quantity}</span>
                        </div>
                      ))
                    ) : application.number_of_pots || application.pot_size ? (
                      // Legacy format fallback
                      <div className="pot-item">
                        <span className="pot-size">{application.pot_size?.pot || 'Unknown Size'}</span>
                        <span className="pot-quantity">× {application.number_of_pots || 'Unknown'}</span>
                      </div>
                    ) : (
                      <p>Not specified</p>
                    )}
                  </div>
                </div>
                <div className="info-item">
                  <label>Number of Carts</label>
                  <p>{application.number_of_carts || 'Not specified'}</p>
                </div>
              </div>
            </div>
            
            {/* Personnel Section */}
            <div className="info-section">
              <div className="section-header">
                <h3><i className="icon-users"></i>Project Personnel</h3>
              </div>
              {application.users && application.users.length > 0 ? (
                <div className="personnel-grid">
                  {application.users.map((person, index) => {
                    // Handle both populated role object and string role
                    const roleName = typeof person.role === 'object' ? person.role?.role : person.role;
                    const roleClass = roleName?.toLowerCase() || 'default';
                    
                    return (
                      <div key={index} className={`personnel-card ${isUserDeleted(person) ? 'deleted-user' : ''}`}>
                        <div className="personnel-header">
                          <h4>{getUserDisplayName(person)}</h4>
                          <span className={`role-badge role-${roleClass}`}>{roleName || 'No Role'}</span>
                        </div>
                        <div className="personnel-details">
                          <p><strong>Email:</strong> {getUserDisplayEmail(person)}</p>
                          <p><strong>Department:</strong> {person.department?.department_name || person.customDepartment || 'Not specified'}</p>
                          <p><strong>Status:</strong> {getUserStatusDisplay(person)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="no-data">No personnel information available</p>
              )}
            </div>

            {/* Experiment Conditions Section */}
            <div className="info-section">
              <div className="section-header">
                <h3><i className="icon-conditions"></i>Experiment Conditions</h3>
              </div>
              {application.experiment_conditions && application.experiment_conditions.length > 0 ? (
                <div className="conditions-grid">
                  {application.experiment_conditions.map((condition, index) => (
                    <div key={index} className="condition-card">
                      <h4>Condition Set {index + 1}</h4>
                      <div className="condition-details">
                        <div className="condition-row">
                          <div className="condition-item">
                            <label>Photo Period</label>
                            <p>{condition.photo_on && condition.photo_off ? 
                              `${formatClock(condition.photo_on)} - ${formatClock(condition.photo_off)}` : 'Not specified'}</p>
                          </div>
                          <div className="condition-item">
                            <label>Night Interrupt</label>
                            <p>{condition.night_interrupt_on && condition.night_interrupt_off ? 
                              `${formatClock(condition.night_interrupt_on)} - ${formatClock(condition.night_interrupt_off)}` : 'Not specified'}</p>
                          </div>
                        </div>
                        <div className="condition-row">
                          <div className="condition-item">
                            <label>Temperature (Day/Night)</label>
                            <p>{condition.temp_day && condition.temp_night ? 
                              `${condition.temp_day}°C / ${condition.temp_night}°C` : 'Not specified'}</p>
                          </div>
                          <div className="condition-item">
                            <label>CO₂ (Day/Night)</label>
                            <p>{condition.co2_day && condition.co2_night ? 
                              `${condition.co2_day}ppm / ${condition.co2_night}ppm` : 'Not specified'}</p>
                          </div>
                        </div>
                        <div className="condition-row">
                          <div className="condition-item">
                            <label>Humidity (Day/Night)</label>
                            <p>{condition.humidity_day && condition.humidity_night ? 
                              `${condition.humidity_day}% / ${condition.humidity_night}%` : 'Not specified'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">No experiment conditions specified</p>
              )}
            </div>

            {/* Experiment Details Section */}
            <div className="info-section">
              <div className="section-header">
                <h3><i className="icon-details"></i>Experiment Details</h3>
              </div>
              {application.experiment_details && application.experiment_details.length > 0 ? (
                <div className="details-grid">
                  {application.experiment_details.map((detail, index) => (
                    <div key={index} className="detail-card">
                      <h4>Detail Set {index + 1}</h4>
                      <div className="detail-content">
                        <div className="detail-item">
                          <label>Water Type</label>
                          <p>{detail.water?.description || 'Not specified'}</p>
                        </div>
                        <div className="detail-item">
                          <label>Substrate Type</label>
                          <p>{detail.substrate?.description || 'Not specified'}</p>
                        </div>
                        <div className="detail-item">
                          <label>Species</label>
                          <p>{detail.species?.name ? `${detail.species.name}${detail.species.scientific_name ? ` - ${detail.species.scientific_name}` : ''}` : detail.species_not_listed || 'Not specified'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">No experiment details specified</p>
              )}
            </div>

            {/* Safety and Compliance */}
            <div className="info-section">
              <div className="section-header">
                <h3><i className="icon-safety"></i>Safety & Compliance</h3>
              </div>
              <div className="safety-grid">
                <div className="safety-item">
                  <label>Uses Transgenic Plants</label>
                  <span className={`boolean-badge ${application.uses_transgenic_plants ? 'yes' : 'no'}`}>
                    {application.uses_transgenic_plants ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="safety-item">
                  <label>Uses Pathogen</label>
                  <span className={`boolean-badge ${application.uses_pathogen ? 'yes' : 'no'}`}>
                    {application.uses_pathogen ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="safety-item">
                  <label>Safety Training Verified</label>
                  <span className={`boolean-badge ${application.safety_training_verified ? 'yes' : 'no'}`}>
                    {application.safety_training_verified ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            {/* Project Description */}
            <div className="info-section">
              <div className="section-header">
                <h3><i className="icon-description"></i>Project Description</h3>
              </div>
              <div className="description-content">
                <div className="description-item">
                  <label>Project Timeline</label>
                  <p>{application.project_timeline || 'Not specified'}</p>
                </div>
                <div className="description-item">
                  <label>Experimental Design Summary</label>
                  <p>{application.experimental_design_summary || 'Not specified'}</p>
                </div>
                <div className="description-item">
                  <label>Special Environmental Requirements</label>
                  <p>{application.special_environmental_requirements || 'None specified'}</p>
                </div>
                <div className="description-item">
                  <label>Additional Comments</label>
                  <p>{application.additional_comments || 'None provided'}</p>
                </div>
              </div>
            </div>
            
            {/* Funding Information */}
            {(application.funding && application.funding.length > 0) || application.account_number ? (
              <div className="info-section">
                <div className="section-header">
                  <h3><i className="icon-funding"></i>Funding Information</h3>
                </div>
                <div className="funding-content">
                  {application.funding && application.funding.length > 0 ? (
                    application.funding.map((fund, index) => (
                      <div key={index} className="funding-item">
                        <p><strong>Account Number:</strong> {displayAccountNumber(fund.account_number) || 'Not specified'}</p>
                        <p><strong>Source of Funds:</strong> {fund.source_of_funds || 'Not specified'}</p>
                      </div>
                    ))
                  ) : application.account_number ? (
                    <div className="funding-item">
                      <p><strong>Account Number:</strong> {displayAccountNumber(application.account_number)}</p>
                      <p><strong>Source of Funds:</strong> {application.source_of_funds || 'Not specified'}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* Comments */}
            {application.comments && application.comments.length > 0 && (
              <div className="info-section">
                <div className="section-header">
                  <h3><i className="icon-comments"></i>Comments</h3>
                </div>
                <div className="comments-content">
                  {application.comments
                    .slice(0, 5) // Show only the 5 most recent comments
                    .map((comment, index) => (
                      <div key={index} className="comment-card">
                        <div className="comment-header">
                          <strong>{comment.commenter_name}</strong>
                          <span className="comment-role">({comment.commenter_role})</span>
                          <span className="comment-date">{formatDate(comment.timestamp)}</span>
                        </div>
                        <p>{comment.comment}</p>
                        {comment.comment_type && (
                          <span className="comment-type-tag">{comment.comment_type.replace('_', ' ')}</span>
                        )}
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="saved-applications">
      <h1>My Applications</h1>
      {loading && <p>Loading applications...</p>}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchApplications}>Retry</button>
        </div>
      )}
      
      {!loading && !error && applications.length === 0 && (
        <p>No applications found.</p>
      )}

      {!loading && applications.length > 0 && (
        <div className="applications-tables">
          {/* Saved Applications */}
          <div className="saved-applications-section">
            <h2>Saved Applications</h2>
            <table>
              <thead>
                <tr>
                  <th>Project Title</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications
                  .filter(app => app.project_status === 'Saved')
                  .map((app) => (
                    <tr key={app._id}>
                      <td>{app.project_title || 'Untitled'}</td>
                      <td>{formatDateSafe(app.project_start_date)}</td>
                      <td>{formatDateSafe(app.project_end_date)}</td>
                      <td className="actions-cell">
                        <button onClick={() => handleView(app)}>View</button>
                        <button onClick={() => handleEdit(app)}>Edit</button>
                        <button onClick={() => handleDelete(app._id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Submitted Applications */}
          <div className="submitted-applications-section">
            <h2>Submitted Applications</h2>
            <table>
              <thead>
                <tr>
                  <th>Project Title</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications
                  .filter(app => app.project_status === 'Submitted')
                  .map((app) => (
                    <tr key={app._id}>
                      <td>{app.project_title || 'Untitled'}</td>
                      <td>{formatDateSafe(app.project_start_date)}</td>
                      <td>{formatDateSafe(app.project_end_date)}</td>
                      <td className="actions-cell">
                        <button onClick={() => handleView(app)}>View</button>
                        <button onClick={() => handleWithdraw(app._id)}>Withdraw</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Approved Applications */}
          <div className="approved-applications-section">
            <h2>Approved Applications</h2>
            <table>
              <thead>
                <tr>
                  <th>Project Title</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications
                  .filter(app => ['Approved', 'Expired', 'Terminated', 'In Progress', 'Completed', 'Revision Required', 'Revised'].includes(app.project_status))
                  .map((app) => (
                    <tr key={app._id}>
                      <td>{app.project_title || 'Untitled'}</td>
                      <td>{formatDateSafe(app.project_start_date)}</td>
                      <td>{formatDateSafe(app.project_end_date)}</td>
                      <td className="actions-cell">
                        <button onClick={() => handleView(app)}>View</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {viewingApplication && (
        <ApplicationView 
          application={viewingApplication} 
          onClose={() => setViewingApplication(null)} 
        />
      )}

      {editingApplication && (
        <EditApplicationModal
          application={editingApplication}
          user={user}
          onSave={handleEditSave}
          onClose={handleEditClose}
        />
      )}
    </div>
  );
};

export default SavedApplications;
